import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Navigation, Loader2, Building2, Home, X } from 'lucide-react';

/**
 * Smart Location Search Component - Realtor.ca Style
 * 
 * Features:
 * - Real-time predictions as user types
 * - Two groups: Locations (cities, regions) and Addresses (full street addresses)
 * - Instant search on selection (no ENTER required)
 * - Canada-only restriction
 */
const SmartLocationSearch = ({
  onLocationSelect,
  placeholder = 'Search by city, postal code, or address...',
  className = '',
}) => {
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const debounceTimerRef = useRef(null);
  
  const [inputValue, setInputValue] = useState('');
  const [predictions, setPredictions] = useState({ locations: [], addresses: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Load Google Maps Script
  useEffect(() => {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) return;

    if (window.google?.maps?.places) {
      setIsScriptLoaded(true);
      return;
    }

    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      const checkLoaded = setInterval(() => {
        if (window.google?.maps?.places) {
          setIsScriptLoaded(true);
          clearInterval(checkLoaded);
        }
      }, 100);
      setTimeout(() => clearInterval(checkLoaded), 10000);
      return () => clearInterval(checkLoaded);
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => setIsScriptLoaded(true);
    document.head.appendChild(script);
  }, []);

  // Initialize services
  useEffect(() => {
    if (!isScriptLoaded) return;
    
    autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
    
    // Create a dummy div for PlacesService (required by Google)
    const dummyDiv = document.createElement('div');
    placesServiceRef.current = new window.google.maps.places.PlacesService(dummyDiv);
  }, [isScriptLoaded]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch predictions
  const fetchPredictions = useCallback((query) => {
    if (!autocompleteServiceRef.current || query.length < 2) {
      setPredictions({ locations: [], addresses: [] });
      return;
    }

    setIsLoading(true);

    // Fetch locations (cities, regions)
    const locationRequest = {
      input: query,
      componentRestrictions: { country: 'ca' },
      types: ['(regions)'] // cities, provinces, postal codes
    };

    // Fetch addresses (street addresses)
    const addressRequest = {
      input: query,
      componentRestrictions: { country: 'ca' },
      types: ['address']
    };

    Promise.all([
      new Promise((resolve) => {
        autocompleteServiceRef.current.getPlacePredictions(locationRequest, (results, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
            resolve(results.slice(0, 4)); // Max 4 locations
          } else {
            resolve([]);
          }
        });
      }),
      new Promise((resolve) => {
        autocompleteServiceRef.current.getPlacePredictions(addressRequest, (results, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
            resolve(results.slice(0, 3)); // Max 3 addresses
          } else {
            resolve([]);
          }
        });
      })
    ]).then(([locations, addresses]) => {
      setPredictions({ locations, addresses });
      setIsLoading(false);
      setHighlightedIndex(-1);
    }).catch(() => {
      setIsLoading(false);
    });
  }, []);

  // Debounced input handler
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setShowDropdown(true);

    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce predictions fetch (300ms)
    debounceTimerRef.current = setTimeout(() => {
      fetchPredictions(value);
    }, 300);
  };

  // Get place details and trigger search
  const handleSelectPrediction = (prediction) => {
    if (!placesServiceRef.current) return;

    setIsLoading(true);
    setShowDropdown(false);
    setInputValue(prediction.description);

    placesServiceRef.current.getDetails(
      {
        placeId: prediction.place_id,
        fields: ['geometry', 'formatted_address', 'address_components', 'name', 'types']
      },
      (place, status) => {
        setIsLoading(false);
        
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place?.geometry?.location) {
          const locationData = parsePlace(place, prediction);
          onLocationSelect?.(locationData);
        }
      }
    );
  };

  // Parse place details
  const parsePlace = (place, prediction) => {
    const components = place.address_components || [];
    
    const getComponent = (types) => {
      const component = components.find(c => types.some(type => c.types.includes(type)));
      return component?.long_name || '';
    };

    const getShortComponent = (types) => {
      const component = components.find(c => types.some(type => c.types.includes(type)));
      return component?.short_name || '';
    };

    // Determine if it's a location (city/region) or exact address
    const isLocation = prediction.types?.some(t => 
      ['locality', 'administrative_area_level_1', 'administrative_area_level_2', 'postal_code', 'sublocality'].includes(t)
    );

    return {
      formatted_address: place.formatted_address,
      display_name: prediction.structured_formatting?.main_text || place.name,
      secondary_text: prediction.structured_formatting?.secondary_text || '',
      place_id: prediction.place_id,
      type: isLocation ? 'location' : 'address',
      coordinates: {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng()
      },
      city: getComponent(['locality', 'sublocality', 'administrative_area_level_3']),
      province: getShortComponent(['administrative_area_level_1']),
      postal_code: getComponent(['postal_code']),
      country: 'Canada'
    };
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    const allPredictions = [...predictions.locations, ...predictions.addresses];
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => 
        prev < allPredictions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      handleSelectPrediction(allPredictions[highlightedIndex]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  // Get current location
  const handleGetLocation = () => {
    if (!navigator.geolocation) return;

    setIsLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        try {
          const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${apiKey}`
          );
          const data = await response.json();
          
          if (data.results?.[0]) {
            const result = data.results[0];
            const components = result.address_components || [];
            
            const getComponent = (types) => {
              const component = components.find(c => types.some(type => c.types.includes(type)));
              return component?.long_name || '';
            };

            const getShortComponent = (types) => {
              const component = components.find(c => types.some(type => c.types.includes(type)));
              return component?.short_name || '';
            };

            const locationData = {
              formatted_address: result.formatted_address,
              display_name: 'Current Location',
              type: 'location',
              coordinates: { lat: latitude, lng: longitude },
              city: getComponent(['locality', 'sublocality']),
              province: getShortComponent(['administrative_area_level_1']),
              postal_code: getComponent(['postal_code']),
              country: 'Canada'
            };

            setInputValue(result.formatted_address);
            setShowDropdown(false);
            onLocationSelect?.(locationData);
          }
        } catch (error) {
          console.error('Geocoding error:', error);
        } finally {
          setIsLoading(false);
        }
      },
      () => {
        setIsLoading(false);
        alert('Could not get your location. Please enable location access.');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Clear input
  const handleClear = () => {
    setInputValue('');
    setPredictions({ locations: [], addresses: [] });
    setShowDropdown(false);
    inputRef.current?.focus();
  };

  const allPredictions = [...predictions.locations, ...predictions.addresses];
  const hasResults = allPredictions.length > 0;

  return (
    <div className={`relative ${className}`}>
      {/* Input Field */}
      <div className="relative">
        <MapPin className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 sm:w-5 h-4 sm:h-5 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => inputValue.length >= 2 && setShowDropdown(true)}
          placeholder={placeholder}
          disabled={!isScriptLoaded}
          className="w-full pl-10 sm:pl-12 pr-20 sm:pr-24 py-3 sm:py-3.5 text-sm sm:text-base border-2 border-gray-200 dark:border-gray-600 rounded-lg sm:rounded-xl 
            bg-white dark:bg-gray-800 text-gray-900 dark:text-white
            focus:border-red-500 focus:ring-2 focus:ring-red-500/20 focus:outline-none
            placeholder:text-gray-400 transition-all"
        />
        
        {/* Right side buttons */}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {isLoading && (
            <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
          )}
          {inputValue && !isLoading && (
            <button
              type="button"
              onClick={handleClear}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          )}
          <button
            type="button"
            onClick={handleGetLocation}
            disabled={isLoading}
            className="p-2 bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 
              rounded-lg text-red-500 transition-colors"
            title="Use my current location"
          >
            <Navigation className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Predictions Dropdown */}
      {showDropdown && (inputValue.length >= 2 || hasResults) && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 w-full mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-xl 
            border border-gray-200 dark:border-gray-700 overflow-hidden"
        >
          {isLoading && !hasResults ? (
            <div className="p-4 text-center text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
              <span className="text-sm">Searching...</span>
            </div>
          ) : hasResults ? (
            <div className="max-h-80 overflow-y-auto">
              {/* Locations Group */}
              {predictions.locations.length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700/50 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-2">
                    <Building2 className="w-3.5 h-3.5" />
                    Locations
                  </div>
                  {predictions.locations.map((prediction, index) => (
                    <button
                      key={prediction.place_id}
                      onClick={() => handleSelectPrediction(prediction)}
                      className={`w-full px-4 py-3 text-left hover:bg-red-50 dark:hover:bg-red-900/20 
                        transition-colors flex items-start gap-3 border-b border-gray-100 dark:border-gray-700/50
                        ${highlightedIndex === index ? 'bg-red-50 dark:bg-red-900/20' : ''}`}
                    >
                      <div className="mt-0.5 p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                        <Building2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white truncate">
                          {prediction.structured_formatting?.main_text || prediction.description}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                          {prediction.structured_formatting?.secondary_text || ''}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Addresses Group */}
              {predictions.addresses.length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700/50 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider flex items-center gap-2">
                    <Home className="w-3.5 h-3.5" />
                    Addresses
                  </div>
                  {predictions.addresses.map((prediction, index) => {
                    const adjustedIndex = predictions.locations.length + index;
                    return (
                      <button
                        key={prediction.place_id}
                        onClick={() => handleSelectPrediction(prediction)}
                        className={`w-full px-4 py-3 text-left hover:bg-red-50 dark:hover:bg-red-900/20 
                          transition-colors flex items-start gap-3 border-b border-gray-100 dark:border-gray-700/50 last:border-b-0
                          ${highlightedIndex === adjustedIndex ? 'bg-red-50 dark:bg-red-900/20' : ''}`}
                      >
                        <div className="mt-0.5 p-1.5 bg-green-100 dark:bg-green-900/30 rounded-lg">
                          <Home className="w-4 h-4 text-green-600 dark:text-green-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 dark:text-white truncate">
                            {prediction.structured_formatting?.main_text || prediction.description}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                            {prediction.structured_formatting?.secondary_text || ''}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : inputValue.length >= 2 ? (
            <div className="p-4 text-center text-gray-500 text-sm">
              No results found for "{inputValue}"
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default SmartLocationSearch;
